/**
 * © copy catalog — docs/prd/copy-tone.md rendered as a typed nested object.
 *
 * Keys are referenced across the suite as `©surface.key` (e.g. `©deck.cap_reached`).
 * The wire form (error envelopes, push payload `copy_key` fields) is the same string
 * WITHOUT the © sigil: `"deck.cap_reached"` — resolve with `resolveCopy()`.
 *
 * Rules (copy-tone.md §1, enforced by review):
 *  - safety surfaces are dead serious — no announcer, no jokes;
 *  - `{placeholders}` are interpolation slots filled by the client;
 *  - strings here are shippable v1 copy, not placeholders. Verticals may ADD
 *    sections; existing keys are frozen (tone QA checklist applies to additions).
 */

export const copy = {
  onboard: {
    // v6 warm-landing reframe (owner directive 2026-07): the front door is
    // fun, light and no-pressure — the twist mechanics are a surprise inside
    // the app, never the headline. Landing strings below were REPLACED, key
    // names kept (the freeze rule was waived for this reframe).
    tagline: "No such thing as a wrong swipe.",
    panels: {
      1: "You like them, they like you. The classic — just nicer.",
      2: "Match, then chat when you're ready. Women get the first word.",
      3: "A few playful twists that keep swiping fun. You'll see.",
    },
    // the landing's drifting ticker — warm one-liners, overheard not broadcast
    ticker: {
      1: "No pressure. Just people.",
      2: "Say hi whenever you're ready.",
      3: "Nice people nearby.",
      4: "Little surprises inside.",
      5: "Ticker is rooting for you.",
    },
    identity_note:
      "In matches between a woman and a man, she opens the conversation. Everyone else: fastest thumb wins.",
    location:
      "We use your location to show people nearby — that's it. No background tracking, no breadcrumbs. Prefer mystery? Pick a city instead.",
    location_denied: "No GPS, no problem. Pick your city — we'll pretend you didn't just do that.",
    reject_match_reveal:
      "MUTUAL DESTRUCTION. On Humble, two left swipes make a match. Rejection is data. Chemistry is chemistry, even when it's explosive.",
    briefing_reject_note:
      "Fair warning: rejections aren't secret here. People you reject can see it — some of them will match you over it.",
    tone_waiver:
      "Fair warning: this app talks like a game announcer with a grudge. The safety features never joke, and they never stop working. Deal?",
  },

  auth: {
    bad_creds: "Wrong email or password. Rejection hurts, we know. Try again.",
    link_expired:
      "That magic link expired. Like most sparks. We sent you a new one — check your inbox.",
    oauth_fail: "Google said no. It's not you, it's OAuth. Try again or use email.",
    email_taken: "This email already has an account. Past you strikes again — log in instead?",
    verify_wait: "Check your inbox. Yes, the spam folder too. We believe in you slightly.",
    verify_expired: "That verification link is dead. Unlike your chances. We can send another.",
    throttled: "Too many attempts. Take a breath. Try again in a few minutes.",
  },

  deck: {
    swipe_labels: {
      left: "REJECT",
      right: "LIKE",
      super: "SUPER REJECT",
      // --- v5 deck overhaul append (S-10): the crush commit stamp + the
      //     swipe-up hint tag (short pixel labels — v5 §4) ---
      crush: "CRUSH",
      up: "PROFILE",
    },
    empty:
      "Deck's empty. You've judged everyone within {radius} km. Widen the net, grab a Boost, or go be a person for a while.",
    cap_reached:
      "Out of swipes. Even judgment needs a bedtime. Resets in {hh:mm} — or Premium raises the ceiling.",
    card_gone: "That profile left the building. Swipe on.",
    sr_button_tooltip: "Super Reject: 24-hour timeout, delivered personally. {n} left today.",
    crush_button_tooltip: "Crush: the sincere one. Use sparingly, feel everything. {n} left today.",
    rewind_toast: "Undone. We saw nothing.",
    crush_ribbon: "CRUSHED ON YOU",
    rewind_too_late: "Too late — that one already resolved. The universe is fast.",
    // --- v3 "AFTER-DARK ARCADE" append (S-10 restyle — scene states, never a bare panel) ---
    empty_title: "DECK'S EMPTY.",
    loading_quip: "Shuffling tonight's regrets…",
    // --- v5 deck overhaul append (S-10): inline full profile + multi-photo ---
    open_profile: "Open the full profile",
    close_profile: "Close the full profile",
    photo_prev: "Previous photo",
    photo_next: "Next photo",
    profile_hint: "Everything they chose to admit.",
  },

  match: {
    classic_title: "A MATCH. HOW ORIGINAL.",
    classic_body: "You liked each other. The old-fashioned way. Adorable.",
    reject_title: "REJECT-MATCH.",
    reject_body:
      "You two hated each other. At the exact same time. That's not nothing — talk about it.",
    reject_after_sr:
      "They didn't just reject you — they SUPER-rejected you. And yet, here you both are. Destiny has a sense of humor.",
    reject_after_sr_sender:
      "You SUPER-rejected them. They waited out the timeout and rejected you right back — and that's a match now. The audacity is mutual.",
    reject_banner:
      "This match exists because you both swiped left. Start there. It's a great story.",
    wait_your_turn: "She opens this one. House rules.",
    long_fuse_badge: "7-day fuse — hate needs time to bloom",
    expiry_warning: "This match self-destructs in {hh:mm}. Someone say literally anything.",
    crush_context: 'They crushed on this: {element_snapshot} — "{comment}"',
    // --- v3 append — S-12 full-screen reveal kicker lines (small caps above the title) ---
    classic_kicker: "it's mutual",
    reject_kicker: "mutual destruction",
  },

  chat: {
    locked_waiting: "She has the opener. Your move is to wait — gracefully.",
    your_move: "Your serve. One good line beats three bad paragraphs.",
    berserk_unlock: "BERSERKER: opener rights unlocked. Use them like you've had media training.",
    berserk_over:
      "Berserker Mode ended before you hit send. The window closed. The rule is back. Poetry, really.",
    call_locked: "Calls unlock after 3 messages each. Prove you can type before you talk.",
    image_consent: "{name} wants to send you an image. Show images from them?",
    closed: "This conversation has ended. No further questions.",
    tombstone: "Deleted account. They've left the arena.",
    missed_call_line: "Missed {kind} call · {time}",
    gg_prompt: "Went on the date? Both hit GG and make it official for the stat sheet.",
    // V9 append — #94 GG handshake states (SM-13b GG half).
    gg_waiting: "GG sent — waiting on theirs.",
    gg_complete: "GG — both sides confirmed. +1 honor each. The stat sheet remembers.",
    gg_not_open: "GG unlocks once the conversation is actually open. Talk first, honor later.",
    run_it_back: "Run it back? If they agree, this match reopens. Sequels sometimes work.",
    match_expired:
      "Too slow — this match expired while you were typing. Your draft is safe. Run it back?",
    video_optin:
      "Allow video calls in this chat? Voice stays available either way. You can turn this off anytime.",
    opening_move_default: [
      "So — best rejection you've ever gotten?",
      "Describe your week in one red flag.",
      "You get one question. Make it weird.",
    ],
    hidden_by_filters: "Hidden by your filters.",
    send_anyway:
      "This message may come across as harassment. You can still send it — and it stays on the record. Send anyway?",
    unsend_label: "Unsend",
    unsend_confirm:
      "Remove this message for both of you? It stays on our records (reports still see it), and any notification already delivered isn't recalled.",
    message_removed: "Message removed.",
    rate_limited:
      "Thirty messages in a minute — even we're impressed. Your draft is safe; give it a moment.",
    opener_cap_reached:
      "That's every new conversation you get today. Existing chats still work — the counter resets in {hh:mm}.",
    media_cap_reached: "You've hit the hourly media cap. Text still flows — use your words.",
    // --- V3a append — SM-13b extend / Run It Back / picker surfaces (S-15/S-16) ---
    extend_cta: "Add 24 hours",
    extend_done: "+24 hours on the clock. {n} of 2 extensions used.",
    extend_not_applicable: "Nothing to extend here — this fuse already did its job.",
    extend_frozen: "This countdown is frozen right now. Frozen clocks don't take extensions.",
    extend_budget: "Both extensions are spent. The clock is the clock now.",
    extend_no_credit:
      "Your free daily extend is used and you hold no Extend credits. The store sells time — $1.99 a day.",
    rib_waiting: "Run It Back sent. The offer holds for 72 hours. Their move.",
    rib_budget: "You already offered this week. One Run It Back per match per 7 days — desperation has a cap.",
    rib_not_applicable: "This match isn't expired. Run It Back is for the morgue only.",
    gif_search_placeholder: "Search reactions…",
    gif_empty: "No reaction fits? Bold of the situation.",
    presence_online: "Online",
    presence_today: "Active today",
    presence_week: "Active this week",
    voice_recording: "Recording… release to send, slide away to cancel.",
    voice_too_long: "Voice notes cap at 60 seconds. Edit it down — brevity is flirting.",
    media_pending: "Held for a quick review before delivery. Seconds, usually.",
    // --- v3 "AFTER-DARK ARCADE" append — S-16 comms restyle (append-only) ---
    typing: "typing…",
    seen: "Seen",
    send_label: "Send",
    back_label: "Back to matches",
    input_placeholder: "Message",
    tool_gif: "Send a GIF",
    tool_image: "Send an image",
    tool_voice: "Record a voice note",
    voice_play: "Play voice note",
    voice_pause: "Pause voice note",
    empty_classic_note: "Nobody has said anything yet. The silence is technically mutual.",
    tap_to_reveal: "tap to reveal",
  },

  icebreakers: {
    classic_pool: [
      "Two truths and a red flag — go.",
      "What's your villain origin story?",
      "Rank your last three dates. Be brutal.",
      "What should I absolutely not ask you about?",
      "Pitch me the worst possible first date.",
      "What's your most defensible bad opinion?",
      "You have 10 words to be interesting. Timer starts now.",
      "Wrong answers only: what do you do for a living?",
    ],
    reject_pool: [
      "So. What was it about my face?",
      "You rejected me first, I felt it.",
      "Let's compare notes: why'd you swipe left?",
      "We already know the worst-case scenario. Liberating, no?",
      "This is either the best or worst idea this app has had.",
      "Mutual rejection is basically an alibi. Where were you Tuesday?",
      "Rate my profile. You clearly had thoughts.",
      "We start from zero expectations. Only way is up.",
    ],
  },

  cooldown: {
    title: "SUPER-REJECTED.",
    body: "Someone found your profile and made a decision about it. You're benched for {hh:mm} — hidden from decks, swiping disabled. Your chats still work. Your dignity is on its own.",
    reversed_body:
      "Your Super Reject hit a Shield. It bounced. You are now serving the timeout you ordered for someone else — {hh:mm} remaining. There's a lesson here somewhere.",
    whats_open: "Still yours: conversations, calls, the store, and reflection.",
    shield_pitch: "Next time, be holding one of these.",
    back_notification: "Timeout over. The deck missed you. Probably.",
    report_cta: "Report this Super Reject",
    report_cta_sub:
      "We know who sent it. You won't be told, but our team will review them.",
    block_cta: "Block whoever did this",
    block_cta_sub:
      "Blocks the sender without revealing them. They can never reach you again.",
    // --- v3 append — S-26 funereal countdown scene ---
    timer_caption: "until the deck takes you back",
  },

  shield: {
    arm_explainer:
      "Arming burns one Shield and covers you for 60 minutes. If a Super Reject lands in that window, it bounces back — THEY take the 24 hours. If nothing lands, the Shield is spent anyway. That's the bet.",
    armed_hud: "SHIELD UP · {mm:ss}",
    already_armed: "One shield at a time, general. {mm:ss} left on the current one.",
    arm_during_cooldown:
      "You're in a cooldown — nobody can even hit you for {hh:mm} of this window. Arming anyway is a choice. A bold, wasteful choice.",
    blocked_push:
      "SHIELD BLOCK. {name} tried to Super Reject you. It reversed. They're in timeout. You may gloat quietly.",
    fizzled:
      "Your Shield expired unspent. 60 minutes of peace — worst-case scenario for a shield, best case for you.",
    block_log_entry: "Reversed: {name} fired a Super Reject at you · {date}",
    sender_reversed:
      "Your Super Reject was blocked and reversed. You're in cooldown for 24 h.",
    /** V2 append — S-21 ARM with zero balance (402 NO_CREDIT, store deep-link). */
    no_credit:
      "Zero Shields in stock. Protection is sold separately — three for $9.99, no refunds, no exceptions.",
    // --- v3 append — S-21 armed neon timer ring ---
    armed_stamp: "SHIELD UP",
    arm_cta: "ARM — burn one, cover 60 min",
    armed_note:
      "Anything that lands in this window bounces back. After that, the credit is spent either way.",
  },

  sr: {
    landed_push:
      "You've been Super-Rejected. 24-hour cooldown. We won't say who. We WILL say ouch. (You can still report or block the sender — see your timeout screen.)",
    already_dead:
      "They're already in a timeout. Even we think a second one is excessive. Super Reject refunded.",
    quota_out: "No Super Rejects left today. Hatred, like all fine things, is rationed.",
    same_target_capped:
      "You've already Super-Rejected this person recently. One nuke per person per month — we have to insist. Super Reject refunded.",
    // --- game-toggles append (S-27 per-user mechanic opt-outs — sm/README copy-key rule) ---
    disabled:
      "Super Reject is switched off in your settings. The nuke stays in the silo until you flip it back on.",
  },

  announcer: {
    tiers: {
      3: "FIRST BLOOD. Three straight. Someone's insufferable today.",
      5: "RAMPAGE. Five in a row. Have a free Boost. Spend it irresponsibly.",
      7: "DOMINATING. Seven. The deck fears you.",
      10: "GODLIKE. TEN CONSECUTIVE MATCHES. You've earned the right to redecorate an enemy. Enemy Takeover token acquired.",
    },
    beyond: "BEYOND GODLIKE. We didn't write copy for this. Nobody thought you'd get here.",
    broken: "Streak ended at {n}. The flame dies; the loot stays. Boosts and tokens earned are yours.",
    berserk_start: "BERSERKER MODE. Eight hours. Glow on. Manners optional. Caps very much still on.",
    berserk_end: "Berserker Mode has ended. You're a civilian again. It was beautiful.",
    pity: "A week without a match. Here's a free Boost and our sincerest 'it's them, not you.' It might be you. Boost anyway.",
  },

  takeover: {
    offer_sent:
      "Takeover offer delivered. Now they decide whether to hand you the keys. Consent: it's hot.",
    offer_received:
      "{name} wants to run an Enemy Takeover on your profile. Read the terms below. Your call — and your kill switch.",
    offer_canceled: "Offer withdrawn. The canvas stays yours.",
    cancel_offer_label: "Cancel offer",
    consent_terms:
      "If you accept: {name} can edit an OVERLAY of your bio and prompt answers for 24 hours. Your photos, name, age, identity fields and settings are untouchable. Everyone sees a 'HIJACKED BY {name}' badge. You can end it instantly, any time, with one tap. It ends automatically after 24 hours. Every edit is reviewed by moderation rules.",
    badge: "HIJACKED BY {name}",
    accepted_push:
      "{name} accepted your Takeover. The canvas is yours. Moderation is watching. So is everyone else.",
    declined: "Takeover declined. Your token is refunded. Your ego is your problem.",
    edit_rejected:
      "That edit was rejected by moderation and was not published. A second rejection ends the takeover.",
    kill_button_label: "END THIS NOW",
    killed_by_owner: "The owner pulled the plug. Overlay gone. It was fun while it lasted — {duration}.",
    expired: "Takeover complete. The overlay dissolves. History will remember. (It won't.)",
    target_gone:
      "Your takeover target has left the platform. The canvas burned down. Token not refunded — you did spend it on something memorable.",
    offer_locked:
      "Takeover offers unlock once this conversation has a first message. House rules apply even to the GODLIKE.",
    // --- V4 appends (append-only — never edit entries above) ---
    no_token: "No Takeover token. Ten consecutive matches earns you one. Get climbing.",
    offers_disabled: "This player has takeover offers switched off. The canvas is not for sale.",
    offer_pending: "You already have an offer out. One heist at a time.",
    canvas_occupied: "Someone else is already on this canvas. One canvas, one artist.",
    overlay_rejected:
      "That content was rejected by moderation and was not published.", // serious (tone rule 4)
    accept_label: "Accept", // consent buttons are dead serious, equal weight (S-23)
    decline_label: "Decline",
    change_log_title: "Every edit, on the record",
    view_original_label: "View original",
    pending_review: "Pending moderation review",
    // --- overlay public render (append-only) ---
    overlay_marker: "The owner didn't write this. That's the whole point.", // label on overlayed bio/prompts (S-10/S-11)
  },

  berserk: {
    confirm:
      "Activate Berserker Mode? 8 hours starts NOW and does not pause — not for sleep, not for account pause, not for regret. Opener rights in every match, raised caps, maximum glow.",
    blocked_cooldown:
      "You can't go Berserker from inside a timeout. Even legends serve their 24 hours.",
    already_active: "One Berserker at a time. You have {hh:mm} of glory left.",
    store_honesty:
      "Berserker cosmetics suggest invincibility. That part is theater: anyone can still block, report, or unmatch you instantly, and all safety rules apply in full.",
    // V3b append (SM-7 berserker — sm/README copy-key rule)
    hud_active: "BERSERKER · {hh:mm}",
    no_credit: "No Berserker session in stock. The store sells exactly one kind of glory.",
    perks:
      "8 hours of: opener rights in every match, likes and rejects raised to 200/day, opener DMs to 50/day, maximum glow, juggernaut badge, announcer treatment.",
    expired_note: "Sessions never pause and never refund — not for sleep, not for account pause, not for regret.",
    // --- game-toggles append (S-27 per-user mechanic opt-outs — sm/README copy-key rule) ---
    disabled:
      "Berserker Mode is switched off in your settings. No glow until you flip it back on.",
  },

  store: {
    shield_pitch: "3 Shields · $9.99. The only insurance policy with a 60-minute attention span.",
    berserker_pitch:
      "Berserker Mode · $14.99 · 8 hours. One wild night, priced like a month of being reasonable.",
    // berserker_honesty → ©berserk.store_honesty (aliased below in COPY_ALIASES)
    premium_pitch:
      "Humble Premium · $14.99/mo. See your Fans. See your Haters. Filter like a snob. Travel like a ghost.",
    cap_reached: "You're at the {item} cap. Use some before hoarding more. This is an intervention.",
    cap_overflow:
      "A {pack_qty}-pack won't fit: you hold {balance} of {cap}. Spend {overflow_needed} first — we don't sell you things you can't keep.",
    payment_failed:
      "Your Premium payment failed. Your benefits continue until {date} while we retry. Update your card to keep them.",
    success:
      "Purchase complete. Inventory updated. Money can't buy love, but this is measurably adjacent.",
    pending: "Payment confirmed — crediting your arsenal. This takes seconds, not intentions.",
    canceled: "Checkout canceled. Commitment issues confirmed. No charge.",
    restricted: "Purchases are unavailable on this account. Contact support.",
    // V5 append — SM-10/SM-10b server-sent notices (EC-36 / grace refund / duplicate sub).
    cap_refunded:
      "Your purchase no longer fits your {item} cap, so we refunded it in full. Nothing credited, nothing kept — spend some inventory and try again.",
    grace_refunded:
      "This account is closing, so your payment was refunded in full. Money doesn't follow you into the void.",
    duplicate_sub_refunded:
      "You already have Premium — the second subscription was canceled and refunded in full. One of you is enough.",
    // V10 append — v3 store restyle (SANDBOX_MODE treatment + the granted beat).
    sandbox_banner:
      "Sandbox mode: everything is free while we test. Buying grants instantly — no card, no charge, no excuses.",
    sandbox_free: "Sandbox — free",
    granted_sting: "GRANTED.",
    granted_sub: "Inventory updated. Spend it wisely — or don't.",
    lede: "Money can't buy love. It buys everything adjacent.",
  },

  likes: {
    blur_teaser: "{n} fans and {m} haters await. Premium removes the fog of war.",
    fans_empty: "No fans yet. Statistically temporary. Emotionally character-building.",
    haters_empty:
      "Nobody has rejected you yet. Disappointing, honestly — that's half the app.",
    // V10 append — v3 restyle editorial ledes.
    lede: "Everyone who had an opinion about your face.",
    blur_lede: "The fog of war, sold separately.",
  },

  menu: {
    empty: "Menu's finished. The kitchen reopens at midnight.",
    // V10 append — v3 restyle loading line (was hardcoded in the screen) + lede.
    loading: "Plating up…",
    lede: "A handful of people the kitchen stands behind. Today only.",
    reasons: [
      "also thinks pineapple belongs there",
      "rejects with the same energy as you",
      "5 shared interests, 1 shared red flag",
      "active today, unlike your last match",
    ],
  },

  matches: {
    empty:
      "No matches yet. Both kinds count here, so your odds are literally doubled. The deck awaits.",
    expired_empty:
      "Nothing in the morgue. Every match here got a first message in time. Statistically suspicious — well done.",
    // --- V3a append — S-15 section labels ---
    rail_title: "Fresh matches",
    expired_title: "Expired",
    expired_ago: "expired {n}d ago",
    search_placeholder: "Search your matches",
    // --- v3 "AFTER-DARK ARCADE" append — S-15 comms restyle (append-only) ---
    move_you: "YOUR MOVE",
    move_them: "THEIR MOVE",
    move_you_sub: "The fuse burns on your silence.",
    loading_quip: "Fetching your people. And your problems.",
    empty_kicker: "the arena is quiet",
    deck_cta: "To the deck",
    preview_gif: "GIF",
    preview_photo: "Photo",
    preview_voice: "Voice note",
    preview_none: "Say something.",
    preview_none_reject: "Mutual rejection. Discuss.",
    conversations_title: "Conversations",
    // --- game-toggles append — S-15 split lists (like-family vs Reject-Matches) ---
    section_like: "MATCHES",
    section_like_sub: "You said yes at the same time. Suspiciously wholesome.",
    section_reject: "REJECT-MATCHES",
    section_reject_sub: "You hated each other at the exact same time.",
  },

  stats: {
    // V10 append — v3 restyle editorial lede.
    lede: "Own them. Rejection counts as data here.",
    empty: "No numbers yet. Go get rejected — it counts as data.",
    leaderboard_title: "MOST REJECTED THIS WEEK",
    leaderboard_note:
      "Opting in puts your name, photo and weekly rejection count on a leaderboard visible to everyone on Humble. You can opt out anytime — removal is immediate.",
    leaderboard_empty:
      "Nobody's on the board. Either everyone's doing great or nobody's brave enough to check the box.",
  },

  roulette: {
    rules:
      "Roulette pairs you with a random verified human. Camera and mic are optional — text always works. One tap reports; one tap blocks; one tap leaves — during the session and right after it. The last 60 seconds of a session can be attached to a report, for up to 30 minutes after it ends. 18+, verified only, be a person.",
    enter: "Enter the pit",
    need_verified: "Roulette is verified-only. One selfie stands between you and the void.",
    searching: [
      "Finding you a stranger…",
      "Spinning the wheel of regret…",
      "Quality randomness takes a second…",
    ],
    queue_empty:
      "The pit is empty right now. Even chaos sleeps. Try again later or get notified.",
    partner_left: "They're gone. The void provides no exit interviews. Next?",
    vibe_match: "MUTUAL VIBE CHECK PASSED. That's a real match now. The pit delivers.",
    slow_down: "Three skips in ten seconds. Breathe. The wheel unlocks in {s}s.",
    restricted:
      "Roulette is unavailable on your account until {date} following reports. Other features are unaffected.",
    end_card: "That was {name}.",
    end_report_cta: "Report {name}",
    end_report_cta_sub:
      "Filed within 30 minutes of the session, the report includes its last 60 seconds. After that, session details only.",
    end_block_cta: "Block {name}",
    end_block_cta_sub:
      "You will never be paired, matched, or seen by them again. They will not be notified.",
    recent_partners: "Recent partners",
    // --- v3 "AFTER-DARK ARCADE" append — S-18 pit restyle (append-only) ---
    title: "THE PIT",
    tagline: "Random verified strangers. Zero context. Full commitment.",
    queue_kicker: "matchmaking",
    paired_sting: "STRANGER ACQUIRED",
    live_badge: "LIVE",
    connecting_badge: "connecting…",
    next_label: "Next ▸",
    vibe_label: "Vibe Check",
    vibe_sent_label: "Vibe sent",
    leave_label: "Leave",
    leave_pit_label: "Leave the pit",
    next_stranger_label: "Next stranger",
    send_label: "Send",
    input_placeholder: "Say something…",
    av_optional_note: "Optional. Text always works.",
    av_disabled_note: "Text mode tonight — cameras arrive once the relay is verified.",
    profile_hold:
      "The pit can't see you right now — finish your profile first (one live photo minimum).",
    verify_cta: "Get verified",
    cooldown_cta: "You're in cooldown",
    // --- game-toggles append (S-27 per-user mechanic opt-outs — sm/README copy-key rule) ---
    disabled: "The pit is switched off in your settings. Chaos is strictly opt-in.",
    disabled_cta: "Reopen it in settings",
  },

  call: {
    conn_fail:
      "Couldn't connect the call. Networks are the third wheel tonight. Retry, or try voice instead of video.",
    perm_denied:
      "Your browser blocked the {mic_camera}. Check the address-bar permissions — we can't do that part for you.",
    ended_generic: "Call ended.",
    // V3b append (SM-9 calls — sm/README copy-key rule: missing keys are appended, never inlined)
    locked:
      "Calls unlock after {n} messages from each of you. Type first, talk second — radical, we know.",
    video_optin_required:
      "Video needs a yes from both sides. Flip on “Allow video calls” in this chat's ⋯ menu — and get them to do the same.",
    busy: "One call at a time. Finish the current one before starting a sequel.",
    too_late: "That call already moved on without you.",
    incoming: "Incoming {kind} call — {name}",
    ringing: "Calling {name}…",
    connecting: "Connecting…",
    missed: "Missed {kind} call",
    sys_missed: "Missed {kind} call",
    sys_duration: "{kind} call · {mm:ss}",
    turn_unavailable:
      "No relay server is reachable right now. The call will try a direct connection — behind strict networks it may not get through. That's the honest truth.",
    hud_mute: "Mute",
    hud_unmute: "Unmute",
    hud_camera_on: "Camera on",
    hud_camera_off: "Camera off",
    hud_hangup: "Hang up",
    accept: "Accept",
    decline: "Decline",
    idle_title: "Calls",
    idle_explainer:
      "Calls live inside your matches: 3 messages each way unlocks voice; video needs both of you to opt in. No call history is recorded beyond the lines in your chat.",
    // --- v3 "AFTER-DARK ARCADE" append — SM-9 call restyle (append-only) ---
    incoming_kicker: "incoming",
    outgoing_kicker: "calling",
    connecting_kicker: "patching you through",
    ended_kicker: "call over",
    status_live: "In {kind} call",
    cancel_label: "Cancel",
    back_to_matches: "Back to matches",
  },

  pause: {
    explainer:
      "Pause hides you from every deck, grid and Roulette. Your matches and chats stay alive. Come back whenever. Nobody is told.",
    timer_warning:
      "Heads up: your {item} timer keeps running while paused. Time waits for no one, especially not for {price} of it.",
    timer_warning_shield_note:
      "Also: nobody can Super-Reject you while you're paused, so a paused Shield is guarding an empty castle.",
    banner: "You're paused. Invisible to new people; old conversations still work. — [Unpause]",
    arsenal_locked:
      "You're paused — invisible and untouchable. You can't start new timers while paused; anything already running keeps burning. Unpause first; then go wild. — [Unpause]",
  },

  arsenal: {
    empty: "Your arsenal is empty. Peaceful. Vulnerable. The store fixes one of those.",
    incognito_warning:
      "Incognito is on: only people you've already swiped on can see you, so this {item}'s visibility effects will reach almost no one. Turn incognito off for full value — or proceed anyway; the timer won't wait.",
    // V7 append (SM-13a boost — sm/README copy-key rule: missing keys are appended, never inlined)
    boost_already_active: "One Boost at a time. The current one still has time on the clock.",
    boost_active_hud: "BOOSTED · {mm:ss}",
  },

  prompts: {
    pool: [
      "My villain origin story…",
      "The red flag I'm proudest of…",
      "I get irrationally angry about…",
      "My most rejectable quality is…",
      "The hill I will actually die on…",
      "A talent nobody asked for…",
      "My exes would describe me as… (they're wrong)",
      "The lie I tell at parties…",
      "I knew it was over when…",
      "Swipe left if you…",
    ],
  },

  notifications: {
    match_classic: "New match: {name}. You both said yes. Suspiciously wholesome.",
    match_reject: "REJECT-MATCH: {name}. You both said no. This is the good part.",
    message: "{name}: {preview}",
    /** #66 redaction: filtered previews render as this instead — no content, ever. */
    message_redacted: "{name}: New message",
    crush: "Someone crushed on you. An actual crush. Go see.",
    // sr_landed → ©sr.landed_push · shield_block → ©shield.blocked_push ·
    // streak → ©announcer.* · takeover → ©takeover.* · match_expiry → ©match.expiry_warning ·
    // cooldown_return → ©cooldown.back_notification · pity → ©announcer.pity (aliases below)
    roulette: "The pit has people again. Your stranger awaits.",
    call_incoming: "Incoming {kind} call from {name}.",
    call_optout_note: "You may miss calls when Humble is in the background.",
    run_it_back: "{name} wants to run it back. The offer lasts 72 hours.",
    product: "News from Humble: {announcement}",
    mandatory_note:
      "These are always delivered: content-removal and account-safety notices, verification results, and data-export links. They exist to protect you and can't be switched off.",
    // V10 append — v3 restyle editorial lede.
    lede: "Pick your poison. Silence the rest.",
  },

  profile: {
    bio_placeholder:
      "Describe yourself. Lie a little. Everyone does. Not about the important stuff.",
    empty_slots: {
      photos: "A profile with one photo is a rumor. Add more.",
      prompts: "Answer a prompt. Give them something to reject you FOR.",
      voice: "30 seconds of your voice beats 300 words of your typing.",
    },
    need_one_photo: "You need at least one photo. This is a dating app, not witness protection.",
    gender_change_note:
      "You can update this once every 30 days. Existing matches keep their current rules; new matches use the new setting.",
    opening_move:
      "Write your opener once, fire it with one tap when it's your move. Blank = we improvise for you.",
    anthem_placeholder: "One line. A lyric, a warning label, a vibe.",
    power_level: "PROFILE POWER LEVEL: {n}/100",
    // V10 append — v3 restyle editorial lede.
    lede: "The version of you they judge.",
  },

  privacy: {
    hidden_words:
      "Messages containing words or phrases you add here arrive hidden behind a tap-to-reveal notice. The sender is never told. Applies in chats and Roulette. Up to 100 entries.",
  },

  // --- game-toggles append — S-27 › Game features (/settings/features).
  // One label + one honest one-liner per user_settings toggle; the server
  // (SM-2/5/6/7/8) enforces, this screen only flips the switch.
  features: {
    kicker: "your arsenal, your rules",
    title: "Game features",
    lede: "Every mechanic below is optional. Switch one off and the app won't fire it, offer it, or count it for you. The rest of us will still be down here, glowing.",
    saved: "Saved. The machine obeys.",
    save_failed: "Couldn't save that toggle. The machine resists — try again.",
    super_reject_label: "Super Reject",
    super_reject_desc:
      "The 💀 button: 24-hour timeouts, delivered personally. Off hides the button and blocks the fire.",
    berserker_label: "Berserker Mode",
    berserker_desc:
      "8 hours of maximum glow and raised caps. Off blocks activation — sessions in stock keep.",
    killstreak_label: "Killstreak",
    killstreak_desc:
      "Consecutive-match streaks, announcer stings, tier loot. Off keeps your counter at zero — loot already earned survives.",
    roulette_label: "Roulette",
    roulette_desc: "The pit: random verified strangers. Off closes the pit for you, both directions.",
    takeover_label: "Enemy Takeover offers",
    takeover_desc:
      "Whether GODLIKE enemies can OFFER to redecorate your profile. Off refuses new offers; one already running ends from its own screen.",
    read_receipts_label: "Read receipts",
    read_receipts_desc: "Reciprocal: show yours, see theirs. Off hides both directions.",
    presence_label: "Presence dot",
    presence_desc: "Reciprocal: the online dot works both ways. Off means you vanish — and so do they.",
    // Affordance-off states on the feature screens themselves.
    settings_cta: "Game features settings",
    berserker_off_note: "Berserker Mode is switched off in your settings.",
    roulette_off_note: "The pit is switched off in your settings.",
  },

  errors: {
    offline: "You're offline. The judgment economy is paused.",
    500: "Something broke on our side. Not a metaphor — an actual server. Retry, or quote {request_id} at support.",
    404: "This page rejected you. Mutual? Back to the deck.",
    slow: "Still loading. The hamsters are unionized now.",
    maintenance: "Down for maintenance. Even we need to work on ourselves.",
  },

  verify: {
    pending: "Verification submitted. Review usually takes under an hour.",
    approved: "You're verified. The badge is live on your profile.",
    rejected: "Verification unsuccessful: {reason}. You can try again ({n} attempts left today).",
    retry_cap: "Verification attempt limit reached for today. Try again tomorrow.",
  },

  account: {
    export_ready:
      "Your data export is ready. The download link works for 48 hours and requires you to log in.",
  },

  moderation: {
    photo_pending: "This photo is being reviewed and isn't visible yet.",
    photo_rejected: "This photo was rejected: {reason}. Please choose another.",
    image_pending: "Image held for review before delivery.",
    photo_hold:
      "Your last photo was removed by moderation, so your profile is hidden from discovery until you add a new one. Your matches and chats still work. Add a photo to come back.",
    strike_warning:
      "Content you uploaded was removed for violating our rules ({reason}). This is strike {n} of 3 in 90 days. At 2, uploads are suspended for a week; at 3, your account is reviewed for a ban. Even during a suspension, you can always add a replacement profile photo if a removal left you without one.",
    onboarding_pending_gate:
      "All set — your photo is still in review. The moment it's approved, your profile goes live; we'll notify you either way.",
    onboarding_approved:
      "Your photo passed review. Your profile is live — welcome to the deck.",
    /** V8 append — SM-11 resolve(warn): serious moderation warning notice. */
    warning:
      "A moderator reviewed a report about your account ({reason}). Please review our community rules — repeated violations lead to restrictions or a ban.",
  },

  safety: {
    age_gate: "Humble is for adults only. Enter your date of birth. This cannot be changed later.",
    underage:
      "You must be 18 or older to use Humble. This account cannot be created. If you're going through something, support exists: find local youth resources at your national helpline.",
    underage_appeal:
      "If you are 18 or older and this device was locked because someone else used it, you can appeal: email appeal@humble.ekaii.fr with reference {lock_ref} and proof of your age. A human reviews every appeal; a granted appeal unlocks this device immediately.",
    report_ack:
      "Report received. Our team reviews safety reports within 24 hours. The reported person is not told who reported them.",
    block_confirm:
      "Block {name}? You will never see each other again on Humble: no decks, no messages, no Roulette. Any active conversation, call or takeover ends now. They will not be notified. You can unblock later in Settings.",
    block_anonymous_confirm:
      "Block the person who Super-Rejected you? We won't show you who they are, but they will never see you or reach you on Humble again. They will not be notified.",
    banned:
      "Your account has been banned for violating our community rules ({category}). If you believe this is an error, contact appeal@humble.ekaii.fr. You may request a copy of your data within 30 days, or permanently delete your data below.",
    banned_delete:
      "Delete my data: starts the standard 14-day deletion process. Your profile, photos, messages and history will be permanently erased. The ban itself is not lifted by deletion, and records we are legally required to keep (moderation evidence, for up to 90 days) are retained. Type DELETE to confirm.",
    restricted:
      "Access to {surface} is suspended until {date} following moderation review. All other features remain available.",
    delete_confirm:
      "Deleting your account starts a 14-day grace period. During it your profile is hidden and everything is frozen; logging in lets you cancel. After 14 days everything is permanently erased: profile, matches, messages, purchases. Purchased items are not refunded. Type DELETE to confirm.",
    grace_login:
      "This account is scheduled for permanent deletion on {date}. You can cancel deletion now and everything will be restored.",
    /** V8 append — SM-11 reporter closure notice (never discloses the action). */
    report_closed:
      "Your report has been reviewed and closed. Thank you for helping keep Humble safe.",
    /** V8 append — the single sanctioned action disclosure ("we removed the content"). */
    report_closed_removed:
      "Your report has been reviewed: the content was removed. Thank you for helping keep Humble safe.",
  },

  blocklist: {
    /**
     * S-27 blocklist row for via='sr_id' — FULLY ANONYMIZED forever (ARCH §3.1
     * correction 12). Serious surface: identity withheld by design.
     */
    anon_sr: "Blocked: the sender of a Super Reject. Identity withheld — that's the point.",
  },

  // Staff-console append — /admin is an internal ops surface (ARCH §7): plain
  // and factual, no announcer voice (same register as ©moderation/©safety).
  admin: {
    console_title: "Staff console",
    kicker: "Internal",
    nav: {
      overview: "Overview",
      moderation: "Moderation",
      users: "Users",
      verification: "Verification",
    },
    overview: {
      needs_attention: "Needs attention",
      all_clear: "All clear — nothing waiting on staff.",
      open_reports: "Open reports",
      sla_breached: "SLA breached",
      verification_queue: "Verification queue",
      media_review: "Media in review",
      users_total: "Total users",
      active_today: "Active today",
      online_now: "online now",
      new_signups: "New signups",
      matches: "Matches",
      verified_users: "Verified users",
      subscriptions: "Active subscriptions",
      revenue: "Revenue (test-mode)",
      by_lifecycle: "Accounts by lifecycle",
      by_gender: "Accounts by gender",
      match_split: "Match types",
      last_14d: "last 14 days",
      vs_prev_7d: "this week",
      loading_failed: "Couldn't load the overview. Retry.",
    },
    users: {
      search_placeholder: "email / display name / id",
      no_results: "No accounts match that search.",
      restrict: "Restrict a feature",
      restrict_apply: "Apply restriction",
    },
    verification: {
      empty: "No requests in this state.",
      approve: "Approve",
      reject: "Reject",
      revoke: "Revoke badge",
      reason_placeholder: "Reason (shown to the user on reject)",
    },
    queue_empty: "Queue is empty.",
    media_empty: "Nothing in review.",
    load_failed: "Load failed",
    action_failed: "Action failed",
  },

  // V7 append — S-27 Discovery filters (#22/#23) tone strings.
  filters: {
    // V10 append — v3 restyle editorial lede.
    lede: "Curate who gets the privilege of rejecting you.",
    vitals_cap_note:
      "Free tier: any two active filters — your pick, changeable anytime. A third one is where Premium starts filtering like a snob.",
    dealbreaker_hint: "Dealbreaker: hard removes them entirely. Soft just judges them quietly in the ranking.",
    saved: "Filters saved. The deck will judge accordingly.",
  },

  // --- SM-14 append — "Play together" in-chat mini-games (V10; Ticker hosts,
  // the safety rules never joke) ---
  games: {
    this_or_that_label: "This or That",
    two_truths_label: "Two Truths, One Lie",
    tictactoe_label: "Tic-tac-toe",
    invite_push: "{name} challenged you: {game}. Ticker is already keeping score.",
    invite_line: "{name} wants to play {game}.",
    invite_sent: "Challenge sent. The ball — and the pressure — is in their court.",
    declined_line: "They passed on this one. A game declined is not a door closed.",
    invite_expired: "That challenge quietly timed out. Some games are played by not playing.",
    game_abandoned: "This game ended early. No score, no grudges.",
    already_live: "One game at a time in this chat. Finish the current one first — Ticker hates clutter.",
    not_your_turn: "Not your move. Patience is also a strategy.",
    already_picked: "You already locked that round in. No takesies-backsies.",
    illegal_move: "That square is taken. Bold, but taken.",
    over: "This game is already over. The scoreboard is immortal; the board is not.",
    not_your_invite: "You sent this challenge — the answer has to come from them.",
    rate_limited: "Easy, tournament organizer. A few invites a minute is plenty.",
    your_move: "Your move.",
    waiting_partner: "Waiting on their pick. The suspense is the game.",
    reveal_agree: "Same pick. Suspiciously compatible.",
    reveal_differ: "Split decision. Discuss — that's the point.",
    vibe_score: "You vibe {n}/{total}. The differences are conversation starters, not verdicts.",
    two_truths_prompt: "Two of these are true. One is a lie. Choose wisely.",
    two_truths_right: "Called it. The lie was #{n}. Terrifying accuracy.",
    two_truths_wrong: "Wrong — the lie was #{n}. They're better at this than you thought.",
    ttt_win: "{name} takes the board. GG.",
    ttt_draw: "A draw. The only truly diplomatic outcome.",
    // --- SM-14 frontend append — "Play together" UI chrome (V10 web client;
    // Ticker hosts every board, the safety affordances stay dead serious) ---
    play_hint: "Play a game together",
    picker_title: "Play together",
    picker_lede: "Tiny games, zero stakes. Ticker referees and remembers everything.",
    this_or_that_desc: "Five quick either-ors, revealed together. Compatibility, quantified.",
    two_truths_desc: "You write two truths and one lie. They get exactly one guess.",
    tictactoe_desc: "The eternal classic. Three in a row settles it.",
    opener_gate: "Starting a game counts as the opening move — and the opening move isn't yours yet.",
    icebreaker_intro: "Stuck? Ticker's got you.",
    icebreaker_wait: "Openers on display — but the first move is theirs.",
    invite_kicker: "challenge",
    accept_label: "Accept",
    decline_label: "Decline",
    respond_hint: "No pressure. Declining is also an answer.",
    declined_by_you: "You passed on this one. The door stays open.",
    tt_compose_lede: "Three statements about you. Mark the lie — Ticker never tells.",
    tt_statement_placeholder: "Statement {n}",
    tt_mark_lie_label: "This one is the lie",
    tt_send_label: "Send the challenge",
    tt_back_label: "Back",
    tt_guess_confirm: "Lock the guess",
    tt_creator_waiting: "They're reading. Ticker is guarding your lie with its life.",
    tt_lie_marker: "the lie",
    tt_guess_marker: "their guess",
    tt_your_guess_marker: "your guess",
    round_counter: "Round {n} of {total}",
    partner_locked: "They've locked their pick in. Your move.",
    their_move: "Their move. Judge their strategy silently.",
    you_marker: "you",
    them_marker: "them",
    differed_intro: "Where you split — free conversation starters:",
    result_kicker: "final score",
    closed_kicker: "game over",
    ttt_you_are: "You are {mark}.",
    ttt_win_you: "You take the board. GG.",
    ttt_cell_empty: "Square {n}, empty",
    ttt_cell_taken: "Square {n}, {mark}",
    report_game: "Report this game",
  },

  // --- v5 "PIXEL SWEETHEART" append — TICKER, the mascot, speaks (design
  // foundation; short cute-dark lines for the states it anchors) ---
  mascot: {
    name: "Ticker",
    // v6 warm-landing reframe: the host is warm, full stop.
    landing_intro: "Hi, I'm Ticker. I'm rooting for you.",
    peek: "Ticker is judging with you.",
    cheer: "Ticker approves. Loudly.",
    heartbroken: "Ticker felt that one personally.",
    sleepy: "Ticker naps between heartbreaks.",
    sweat: "Ticker is sweating. Something broke, and it wasn't a heart this time.",
  },
} as const;

export type Copy = typeof copy;

/**
 * Aliases — copy-tone.md keys that point at other keys ("→ ©x.y" rows).
 * Wire keys on the LEFT resolve to the canonical entry on the RIGHT.
 */
export const COPY_ALIASES: Readonly<Record<string, string>> = {
  "store.berserker_honesty": "berserk.store_honesty",
  "notifications.sr_landed": "sr.landed_push",
  "notifications.shield_block": "shield.blocked_push",
  "notifications.match_expiry": "match.expiry_warning",
  "notifications.cooldown_return": "cooldown.back_notification",
  "notifications.pity": "announcer.pity",
  "notifications.call_missed": "chat.missed_call_line",
  // V2 append — realtime-protocol §4: the `sr.reversed` frame's dead-serious copy key.
  "sr.reversed": "shield.sender_reversed",
};

/**
 * Resolve a wire copy key ("deck.cap_reached", optionally "©deck.cap_reached")
 * to its string. Returns undefined for unknown keys — callers decide whether
 * that is a 500 (server templates) or a fallback render (client).
 */
export function resolveCopy(key: string): string | undefined {
  const clean = key.startsWith("©") ? key.slice(1) : key;
  const target = COPY_ALIASES[clean] ?? clean;
  let node: unknown = copy;
  for (const part of target.split(".")) {
    if (node === null || typeof node !== "object") return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === "string" ? node : undefined;
}

/**
 * Interpolate `{placeholder}` slots. Missing params are left as-is (the client
 * may fill them later); this is a display helper, never load-bearing logic.
 */
export function formatCopy(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{([\w:]+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(params, name) ? String(params[name]) : whole,
  );
}
